import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { pushSettings } from '@bb/contracts';
import { Button, EmptyState, ErrorState, Field, Input, Modal } from '@bb/ui';
import type { IosThreadBody, IosThreadView } from '../../../api/client.ts';
import { contractIssues, problemFieldErrors, problemText } from './problems.ts';
import { pushKeys, useApi, type PushSettingsPageData } from './usePushSettingsData.ts';

const MESSAGES: Record<string, string> = {
  threadId: 'Letters, digits, _ . - (up to 64).',
  label: 'Enter a label of up to 80 characters.',
};

const checkThread = (draft: IosThreadBody) => {
  const parsed = pushSettings.IosThreadBody.safeParse(draft);
  return parsed.success ? { body: parsed.data, issues: {} as Record<string, string> } : { body: null, issues: contractIssues(parsed.error.issues, MESSAGES) };
};

/**
 * iOS notification threads (BB-77): named `thread-id`s that group a message's notifications on the device. Saving a
 * thread id that exists renames it (and brings an archived one back).
 */
export function IosThreadsTable({ environmentId, data, canEdit }: { environmentId: string; data: PushSettingsPageData; canEdit: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<IosThreadBody>({ threadId: '', label: '' });
  const [touched, setTouched] = useState(false);
  const [renaming, setRenaming] = useState<IosThreadView | null>(null);
  const put = useMutation({
    mutationFn: (body: IosThreadBody) => api.putPushIosThread(environmentId, body),
    onSuccess: async () => {
      setDraft({ threadId: '', label: '' });
      setTouched(false);
      setRenaming(null);
      await queryClient.invalidateQueries({ queryKey: pushKeys.threads(environmentId) });
    },
  });

  if (data.threads.isError) return <ErrorState title="iOS threads could not be loaded" message={problemText(data.threads.error)} onRetry={() => void data.threads.refetch()} />;
  if (!data.threads.data) return <p aria-busy="true">Loading threads…</p>;
  const threads = data.threads.data;
  const checked = checkThread(draft);
  const server = renaming ? {} : problemFieldErrors(put.error);
  // A field is checked once it has a value or the form was submitted, so an empty form starts without errors.
  const issue = (key: keyof IosThreadBody) => (touched || draft[key] !== '' ? checked.issues[key] : undefined) ?? server[key];
  const exists = threads.some((t) => t.threadId === draft.threadId.trim());
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (checked.body) put.mutate(checked.body);
  };

  return (
    <>
      <p className="hint">Pushes with the same thread stack together in Notification Center.</p>
      {threads.length === 0 ? (
        <EmptyState title="No iOS threads" description="iOS groups pushes by app. Add a thread to group, for example, trading signals apart from news." />
      ) : (
        <div className="push-table-scroll">
          <table className="table" aria-label="iOS threads">
            <thead>
              <tr>
                <th scope="col">Thread ID</th>
                <th scope="col">Label</th>
                <th scope="col">Status</th>
                {canEdit && (
                  <th scope="col">
                    <span className="bb-visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {threads.map((t) => (
                <tr key={t.threadId}>
                  <td>
                    <code>{t.threadId}</code>
                  </td>
                  <td>{t.label}</td>
                  <td>{t.archivedAt ? 'Archived' : 'Active'}</td>
                  {canEdit && (
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Rename ${t.threadId}`}
                        onClick={() => {
                          put.reset();
                          setRenaming(t);
                        }}
                      >
                        Rename
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <form className="audience-filter push-thread-form" aria-label="Add thread" onSubmit={submit}>
          <Field label="Thread ID" error={issue('threadId')}>
            <Input value={draft.threadId} spellCheck={false} placeholder="signals" onChange={(e) => setDraft({ ...draft, threadId: e.target.value })} />
          </Field>
          <Field label="Label" error={issue('label')}>
            <Input value={draft.label} placeholder="Trading signals" onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          </Field>
          <Button type="submit" loading={put.isPending && !renaming} disabled={!draft.threadId.trim() || !draft.label.trim()}>
            {exists ? 'Rename thread' : 'Add thread'}
          </Button>
          {put.isError && !renaming && Object.keys(server).length === 0 && <p role="alert">{problemText(put.error)}</p>}
        </form>
      )}
      {renaming && <RenameDialog thread={renaming} busy={put.isPending} error={put.error} onCancel={() => setRenaming(null)} onSave={(label) => put.mutate({ threadId: renaming.threadId, label })} />}
    </>
  );
}

function RenameDialog({ thread, busy, error, onCancel, onSave }: { thread: IosThreadView; busy: boolean; error: unknown; onCancel: () => void; onSave: (label: string) => void }) {
  const [label, setLabel] = useState(thread.label);
  const checked = checkThread({ threadId: thread.threadId, label });
  const server = problemFieldErrors(error);
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onCancel()}
      title={`Rename ${thread.threadId}`}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!checked.body} onClick={() => checked.body && onSave(checked.body.label)}>
            Save
          </Button>
        </>
      }
    >
      <Field label="Label" error={checked.issues.label ?? server.label}>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      {error != null && Object.keys(server).length === 0 && <p role="alert">{problemText(error)}</p>}
    </Modal>
  );
}
